import React from 'react';
import { observer } from 'mobx-react-lite';

import {
  MAX_LENGTH_MESSAGE,
  MESSAGE_LENGTH_ERROR,
  MAX_INPUT_ROWS
} from 'widget/consts/sendMessage';
import { LayoutVersion } from 'widget/types/common';
import type { SendAttachment } from 'widget/types/messages';
import { AttachmentsStore } from 'widget/stores/AttachmentsStore';

import FormBody from './FormBody';
import useEmojiPicker from './useEmojiPicker';

const EMPTY_STRING = '';

export type SendMessageFormProps = {
  className?: string;
  onSendMessage?: (text: string, attachments?: SendAttachment[]) => void;
  onBeforeSend?: () => void;
  attachmentsStore: AttachmentsStore;
  canSendWithoutMessage?: boolean;
  layout?: LayoutVersion;
  submissionFormDisablesSending?: boolean;
};

const SendMessageForm: React.FC<SendMessageFormProps> = ({
  className,
  onSendMessage,
  onBeforeSend,
  attachmentsStore,
  canSendWithoutMessage,
  layout,
  submissionFormDisablesSending
}) => {
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const emojiPicker = useEmojiPicker();

  const trimmedMessage = message.trim();
  const hasValidAttachments = attachmentsStore.hasValidAttachments;
  const hasContent = !!trimmedMessage || hasValidAttachments;
  const isDisabled =
    submissionFormDisablesSending ||
    (!hasContent && !canSendWithoutMessage) ||
    attachmentsStore.hasUploadingAttachments ||
    !!error;
  const isDesktop = layout === LayoutVersion.DESKTOP;

  const handleSendMessage = React.useCallback(async () => {
    onBeforeSend?.();

    if (!hasContent || isDisabled) {
      return;
    }

    const normalizedMessage = trimmedMessage.replace(/\r\n/g, '\n');
    const attachments = attachmentsStore.getAttachmentsForSend();

    onSendMessage?.(
      normalizedMessage,
      attachments.length > 0 ? attachments : undefined
    );
    setMessage(EMPTY_STRING);
    emojiPicker.close();
    attachmentsStore.clearAttachments();
  }, [
    onSendMessage,
    emojiPicker.close,
    onBeforeSend,
    attachmentsStore,
    hasContent,
    isDisabled,
    trimmedMessage
  ]);

  const handleChange = React.useCallback((text: string) => {
    if (text.trim().length > MAX_LENGTH_MESSAGE) {
      setError(MESSAGE_LENGTH_ERROR);
    } else {
      setError(null);
    }
    setMessage(text);
  }, []);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== 'Enter') {
        return;
      }

      if (!event.shiftKey && isDesktop) {
        event.preventDefault();
        handleSendMessage();
      }
    },
    [handleSendMessage, isDesktop]
  );

  const handlePaste = React.useCallback(
    (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const { items } = event.clipboardData;
      const files: File[] = [];

      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === 'file') {
          const file = items[i].getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }

      if (files.length > 0) {
        event.preventDefault();
        attachmentsStore.addFiles(files);
      }
    },
    [attachmentsStore]
  );

  return (
    <FormBody
      maxRows={MAX_INPUT_ROWS}
      className={className}
      message={message}
      error={error}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onChangeMessage={handleChange}
      onSubmitMessage={handleSendMessage}
      disabled={isDisabled}
      attachmentsStore={attachmentsStore}
      emojiPicker={emojiPicker}
    />
  );
};

export default observer(SendMessageForm);
